import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../utils/api";

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

async function readError(response, fallback) {
  try {
    const payload = await response.json();
    if (typeof payload?.detail === "string" && payload.detail.trim()) return payload.detail;
    if (typeof payload?.message === "string" && payload.message.trim()) return payload.message;
    if (typeof payload?.error?.message === "string" && payload.error.message.trim()) {
      return payload.error.message;
    }
  } catch (_) {
    try {
      const text = await response.text();
      if (text.trim()) return text.trim();
    } catch (_) {}
  }
  return fallback;
}

async function requestJson(url, options = {}, fallback = "Ошибка запроса") {
  const response = await apiFetch(url, options);
  if (!response.ok) {
    const err = new Error(await readError(response, fallback));
    err.status = response.status;
    throw err;
  }
  if (response.status === 204) return null;
  return response.json();
}

function stateMeta(status) {
  const effective = status?.state?.effective_status || "";
  if (effective === "active") {
    return { label: "Активен", chipClass: "service-chip is-accent" };
  }
  if (effective === "temp_blocked_device_limit") {
    return { label: "Временно заблокирован", chipClass: "service-chip is-danger" };
  }
  if (effective === "manual_disabled") {
    return { label: "Отключен админом", chipClass: "service-chip" };
  }
  return { label: effective || "Неизвестно", chipClass: "service-chip" };
}

const PLATFORM_GUIDES = {
  ios: [
    "Установите V2RayTune / Happ из App Store.",
    "Скопируйте ссылку подписки ниже.",
    "Добавьте подписку в приложении и обновите конфиг.",
  ],
  android: [
    "Установите V2RayTune / Happ.",
    "Добавьте подписку по URL.",
    "Выберите профиль ITC VPN и подключитесь.",
  ],
  windows: [
    "Установите Happ.",
    "Импортируйте подписку по ссылке.",
    "Обновите подписку и включите профиль ITC VPN.",
  ],
  macos: [
    "Установите Happ.",
    "Добавьте подписку по URL.",
    "Обновите профиль и выберите ITC VPN.",
  ],
  linux: [
    "Используйте sing-box / nekobox / hiddify-cli (если подходит).",
    "Импортируйте subscription URL.",
    "Обновите конфигурацию и активируйте профиль.",
  ],
};

export default function ProxyServicePanel({ apiBase, authHeaders, profile }) {
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [platform, setPlatform] = useState("macos");

  const endpointBase = useMemo(() => `${apiBase}/services/proxy`, [apiBase]);
  const hasStatus = Boolean(status);
  const derivedDevices = devices || status?.devices || { active_count: 0, items: [] };
  const deviceLimit = status?.limits?.device_limit ?? devices?.device_limit ?? 2;
  const meta = stateMeta(status);

  const fetchStatus = async () => {
    setError("");
    try {
      const data = await requestJson(
        `${endpointBase}/me`,
        { headers: authHeaders },
        "Не удалось загрузить статус ITC VPN",
      );
      setStatus(data);
      setDevices(data?.devices || null);
      return data;
    } catch (err) {
      if (err.status === 404) {
        setStatus(null);
        setDevices(null);
        return null;
      }
      throw err;
    }
  };

  const fetchDevices = async () => {
    try {
      const data = await requestJson(
        `${endpointBase}/me/devices`,
        { headers: authHeaders },
        "Не удалось загрузить активные устройства",
      );
      setDevices(data);
      return data;
    } catch (err) {
      if (err.status === 404) {
        return null;
      }
      throw err;
    }
  };

  const ensureAccess = async () => {
    setActionLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await requestJson(
        `${endpointBase}/me/ensure`,
        { method: "POST", headers: authHeaders },
        "Не удалось создать/обновить доступ ITC VPN",
      );
      setStatus(data);
      setDevices(data?.devices || null);
      setNotice("Доступ ITC VPN обновлен. Можно копировать ссылку подписки.");
      return data;
    } catch (err) {
      setError(err.message || "Не удалось получить доступ ITC VPN");
      return null;
    } finally {
      setActionLoading(false);
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchStatus();
      if (data) {
        await fetchDevices();
      }
    } catch (err) {
      setError(err.message || "Не удалось загрузить ITC VPN");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointBase]);

  const copyText = async (value, okMessage) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setNotice(okMessage);
      setError("");
    } catch (_) {
      setError("Не удалось скопировать в буфер обмена");
    }
  };

  const subscriptionUrl = status?.credentials?.subscription_url || "";
  const profileConfig = status?.credentials?.profile || {};
  const deviceItems = safeArray(derivedDevices?.items);
  const platformGuide = PLATFORM_GUIDES[platform] || PLATFORM_GUIDES.macos;
  const uiDisplayName = String(
    status?.display_name || profile?.full_name || profile?.username || "—",
  ).replace(/^ITC Proxy\b/, "ITC VPN");

  return (
    <div className="services-pane proxy-pane">
      <section className="services-block services-block--form proxy-card proxy-card--hero">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Сервис</p>
            <h3 className="services-block__title">ITC VPN</h3>
          </div>
          {hasStatus ? <span className={meta.chipClass}>{meta.label}</span> : null}
        </div>

        <p className="proxy-card__lead">
          Доступ к VPN для участников сообщества. Лимит устройств считается по активным IP за короткое окно времени.
        </p>

        <div className="proxy-hero-grid">
          <div className="service-kv">
            <span>Пользователь</span>
            <strong>{uiDisplayName}</strong>
          </div>
          <div className="service-kv">
            <span>Лимит устройств</span>
            <strong>
              {(derivedDevices?.active_count ?? status?.devices?.active_count ?? 0)}/{deviceLimit}
            </strong>
          </div>
          <div className="service-kv">
            <span>Разблокировка</span>
            <strong>{status?.state?.device_temp_blocked_until ? formatDateTime(status.state.device_temp_blocked_until) : "—"}</strong>
          </div>
          <div className="service-kv">
            <span>Telegram</span>
            <strong>{profile?.username ? `@${String(profile.username).replace(/^@/, "")}` : "—"}</strong>
          </div>
        </div>

        <div className="services-form__actions">
          <button type="button" className="service-btn" onClick={ensureAccess} disabled={actionLoading}>
            {actionLoading ? "Подготовка..." : "Получить / Обновить доступ"}
          </button>
          <button
            type="button"
            className="service-btn service-btn--ghost"
            onClick={() => refreshAll()}
            disabled={loading || actionLoading}
          >
            Обновить статус
          </button>
        </div>

        {!hasStatus && !loading ? (
          <p className="services-placeholder">
            Доступ еще не создан. Нажмите «Получить / Обновить доступ», чтобы выпустить ключ и подписку.
          </p>
        ) : null}

        {error ? <p className="services-feedback services-feedback--error">{error}</p> : null}
        {notice ? <p className="services-feedback services-feedback--success">{notice}</p> : null}
      </section>

      <section className="services-block proxy-card">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Доступ</p>
            <h3 className="services-block__title">Подписка ITC VPN</h3>
          </div>
          {loading ? <span className="service-chip">Загрузка...</span> : null}
        </div>

        {hasStatus ? (
          <div className="proxy-cred-list">
            <div className="proxy-cred-item">
              <span className="proxy-cred-item__label">Ссылка подписки</span>
              <code className="proxy-cred-item__value">{subscriptionUrl || "—"}</code>
              <p className="proxy-guide-note">
                Рекомендуемый способ подключения. Добавьте ссылку в клиент и обновляйте профиль при изменениях.
                Если нужна помощь с настройкой — @FinItcSupportBot.
              </p>
              <div className="proxy-cred-item__actions">
                <button type="button" className="service-btn service-btn--tiny" onClick={() => copyText(subscriptionUrl, "Ссылка подписки скопирована")}>Скопировать</button>
              </div>
            </div>
          </div>
        ) : (
          <p className="services-placeholder">После выдачи доступа здесь появится ссылка подписки ITC VPN.</p>
        )}

        <div className="proxy-profile-grid">
          <div className="service-kv"><span>TLS</span><strong>{profileConfig.type?.includes("reality") ? "Reality" : (profileConfig.type || "—")}</strong></div>
          <div className="service-kv"><span>Отпечаток</span><strong>{profileConfig.fingerprint || "chrome"}</strong></div>
          <div className="service-kv"><span>Имя сервера (SNI)</span><strong>{profileConfig.sni || "deepl.com"}</strong></div>
          <div className="service-kv"><span>Хост</span><strong>{profileConfig.server ? `${profileConfig.server}:${profileConfig.port || 443}` : "—"}</strong></div>
          <div className="service-kv"><span>Публичный ключ</span><strong className="proxy-break">{profileConfig.public_key || "Появится после включения Reality"}</strong></div>
          <div className="service-kv"><span>Short ID</span><strong>{profileConfig.short_id || "Появится после включения Reality"}</strong></div>
        </div>
      </section>

      <section className="services-block proxy-card">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Устройства</p>
            <h3 className="services-block__title">Активные IP</h3>
          </div>
          <div className="services-block__stat-row">
            <span className="service-chip">Сейчас: {derivedDevices?.active_count ?? 0}/{deviceLimit}</span>
            <span className="service-chip">Окно: {status?.limits?.active_window_seconds ?? devices?.active_window_seconds ?? 180} сек</span>
          </div>
        </div>

        {deviceItems.length ? (
          <div className="services-list proxy-devices-list">
            {deviceItems.map((item, index) => (
              <article className="service-card proxy-device-card" key={`${item.ip}-${item.last_seen_at}-${index}`}>
                <div className="service-card__content">
                  <div className="service-card__head">
                    <div>
                      <p className="service-card__eyebrow">IP #{index + 1}</p>
                      <h4 className="service-card__title proxy-device-card__title">{item.ip}</h4>
                    </div>
                  </div>
                  <div className="service-card__meta-grid">
                    <div>
                      <span className="service-card__meta-label">Первая активность</span>
                      <p className="service-card__meta-value">{formatDateTime(item.first_seen_at)}</p>
                    </div>
                    <div>
                      <span className="service-card__meta-label">Последняя активность</span>
                      <p className="service-card__meta-value">{formatDateTime(item.last_seen_at)}</p>
                    </div>
                  </div>
                </div>
                <div className="service-card__actions">
                  <button type="button" className="service-btn service-btn--ghost service-btn--tiny" onClick={() => copyText(item.ip, "IP скопирован")}>Скопировать IP</button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="services-placeholder">
            Активные IP пока не зафиксированы. Подключите VPN и обновите статус через 5–10 секунд.
          </p>
        )}
      </section>

      <section className="services-block proxy-card">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Инструкция</p>
            <h3 className="services-block__title">Как подключиться</h3>
          </div>
          <div className="services-tabs proxy-platform-tabs" role="tablist" aria-label="Платформа клиента VPN">
            {[
              ["ios", "iOS"],
              ["android", "Android"],
              ["windows", "Windows"],
              ["macos", "macOS"],
              ["linux", "Linux"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={platform === id}
                className={`services-tab${platform === id ? " is-active" : ""}`}
                onClick={() => setPlatform(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <ol className="proxy-guide-list">
          {platformGuide.map((step, idx) => (
            <li key={`${platform}-${idx}`}>{step}</li>
          ))}
        </ol>
        <p className="proxy-guide-note">
          Если видите временную блокировку — это означает, что зафиксировано более 2 активных IP за короткий интервал.
          Проверяйте вкладку “Активные IP” или команду /devices в Telegram.
        </p>
      </section>
    </div>
  );
}
